import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import SiteNav from "./SiteNav";
import SiteFooter from "./SiteFooter";
import { DemoModalProvider } from "./DemoModal";
import "./styles/base.css";

// Shared layout for every marketing-site page. Owns the `.shadow-site` scoping wrapper
// (so the site CSS can never leak into the React app), the document title, the nav/footer,
// and hash-scroll behavior (React Router v7 does not auto-scroll to #anchors).
export default function SiteLayout({
  page,
  title,
  navVariant = "flat",
  navActive,
  children,
}) {
  const location = useLocation();
  const rootRef = useRef(null);

  useEffect(() => {
    if (title) document.title = title;
  }, [title]);

  // Reveal-on-scroll for any `.reveal` element on the page. `.reveal` starts at
  // opacity:0 (base.css); this fades them in once visible. Covers every site page
  // (the landing page also runs its own observer in useDemoLoops — harmless overlap).
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const els = root.querySelectorAll(".reveal");
    if (!els.length) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("visible");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [location.pathname]);

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) {
        el.scrollIntoView();
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [location.pathname, location.hash]);

  return (
    <div className={`shadow-site page-${page}`} ref={rootRef}>
      <DemoModalProvider>
        <SiteNav variant={navVariant} active={navActive} />
        {children}
        <SiteFooter />
      </DemoModalProvider>
    </div>
  );
}
